<template>
  <div class="web-container">
    <header class="top-content">
      <h1>C-GRAN Web</h1>
      <div class="divider-line"></div>
      <img class="logo" src="../assets/logo.png" alt="" width="140px" />
    </header>
    <div class="web-main-content">
      <el-row :gutter="36" type="flex">
        <el-col :span="8">
          <img src="../assets/workflow.png" alt="" srcset="" width="100%" />
          <h2>Workflow</h2>
        </el-col>
        <el-col :span="16">
          <section class="function-item upload-area">
            <header>
              <i class="iconfont el-icon-caret-bottom"></i>
              <h3>Input Files</h3>
            </header>
            <div class="item-body">
              <div class="input-item">
                <span class="label"> Intensity File: </span>
                <el-upload
                  class="upload-item"
                  :action="`https://cgran.acemap.info/api/v1/parameter/upload/${uuid}/Intensity File`"
                  :multiple="false"
                  :accept="typeMap['Intensity File']"
                  :limit="1"
                  :file-list="IntensityFileList"
                  :on-success="IntensityFileSuccess"
                >
                  <el-button size="medium" type="info">Upload (.txt)</el-button>
                </el-upload>
                <span class="link" @click="downloadFile('Intensity File')"
                  >Download the Example file</span
                >
              </div>
              <div class="input-item">
                <span class="label"> Seed Node File: </span>
                <el-upload
                  class="upload-item"
                  :action="`https://cgran.acemap.info/api/v1/parameter/upload/${uuid}/Seed Node File`"
                  :accept="typeMap['Seed Node File']"
                  :multiple="false"
                  :limit="1"
                  :file-list="SeedNodeFileList"
                  :on-success="SeedNodeFileSuccess"
                >
                  <el-button size="medium" type="info">Upload (.csv)</el-button>
                </el-upload>
                <span class="link" @click="downloadFile('Seed Node File')"
                  >Download the Example file</span
                >
              </div>
              <div class="input-item">
                <el-tooltip
                  class="item"
                  effect="dark"
                  placement="top"
                  popper-class="popover-content"
                >
                  <div slot="content">
                    <p>
                      1、We recommend that you only select Seed Nodes and their
                      highly correlated substances to generate the molecular
                      network file. If you follow this suggestion, you can click
                      <a
                        href="https://github.com/JeremyChou28/C_GRAN"
                        target="_blank"
                        >this link</a
                      >
                      to use the C-GRAN command line tool for processing so that
                      you can get a list of highly correlated substances before
                      generating the molecular network file.
                    </p>
                    <br />
                    <p>
                      2、We recommend that you use a lower fragment score
                      connectivity threshold when generating molecular network
                      files, for example, 0.20 (maximum is 1.0).
                    </p>
                  </div>
                  <span class="label tip"> Molecular Network File: </span>
                </el-tooltip>
                <el-upload
                  class="upload-item"
                  :action="`https://cgran.acemap.info/api/v1/parameter/upload/${uuid}/Molecular Network File`"
                  :accept="typeMap['Molecular Network File']"
                  :multiple="false"
                  :limit="1"
                  :file-list="MolecularFileList"
                  :on-success="MolecularFileSuccess"
                >
                  <el-button size="medium" type="info">Upload (.csv)</el-button>
                </el-upload>
                <span
                  class="link"
                  @click="downloadFile('Molecular Network File')"
                  >Download the Example file</span
                >
              </div>
            </div>
          </section>
          <section class="function-item">
            <header>
              <i class="iconfont el-icon-caret-bottom"></i>
              <h3>Parameters</h3>
            </header>
            <div class="item-body">
              <div style="display: flex; justify-content: space-between">
                <div class="input-item" style="width: 50%">
                  <span class="label"> Num. of Compounds: </span>
                  <el-input
                    class="input-box"
                    v-model="formList.compounds_num"
                    placeholder=""
                  ></el-input>
                </div>
                <div
                  class="input-item"
                  style="width: 50%; justify-content: center"
                >
                  <span class="label"> Num. of Samples: </span>
                  <el-input
                    class="input-box"
                    v-model="formList.samples_num"
                    placeholder=""
                  ></el-input>
                </div>
              </div>
              <div class="input-item">
                <span class="label"> Minimum High-Correlated Score: </span>
                <el-input
                  class="input-box"
                  v-model="formList.correlation_threshold"
                  placeholder="0.7"
                ></el-input>
                <span class="link no-underline">(0-1)</span>
              </div>
              <div class="input-item">
                <span class="label">
                  RT Tolerance for In-source Fragments Identificaton (min):
                </span>
                <el-input
                  class="input-box"
                  v-model="formList.RT_threshold"
                  placeholder="0.01"
                ></el-input>
              </div>
              <div class="input-item">
                <span class="label">
                  Parent Mass Tolerance for Database Search (ppm):
                </span>
                <el-input
                  class="input-box"
                  v-model="formList.ppm_threshold"
                  placeholder="5"
                ></el-input>
              </div>
              <div class="input-item">
                <el-checkbox v-model="formList.is_filter_element"></el-checkbox>
                <span class="label"
                  >Allowed Elements for Database Search :</span
                >
                <el-select
                  class="input-box"
                  v-model="formList.element_set"
                  placeholder="C"
                  multiple
                >
                  <el-option
                    v-for="item in elementOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  >
                  </el-option>
                </el-select>
              </div>
              <div class="input-item">
                <span class="label"> Minimum Structure Similarity Score: </span>
                <el-input
                  class="input-box"
                  v-model="formList.tanimoto_similarity_threshold"
                  placeholder="0.5"
                ></el-input>
                <span class="link no-underline">(0 - 1)</span>
              </div>
              <div class="input-item">
                <el-checkbox v-model="formList.is_max_iterations"></el-checkbox>
                <span class="label">
                  Maximum Iteration Number of Annotations:
                </span>
                <el-input
                  class="input-box"
                  v-model="formList.max_iterations"
                  placeholder="20"
                ></el-input>
                <span class="link no-underline">(0 - 100)</span>
              </div>
            </div>
          </section>
          <section class="function-item">
            <header>
              <i class="iconfont el-icon-caret-bottom"></i>
              <h3>Export Files</h3>
            </header>
            <div class="item-body">
              <div class="input-item">
                <span class="label"> Compound Annotation Result: </span>
                <span class="label" style="margin-left: 16px">Top </span>
                <el-input
                  class="input-box"
                  v-model="formList.top_k"
                  placeholder="1"
                ></el-input>
                <span class="link no-underline">(1 - 10)</span>
              </div>
            </div>
          </section>
          <section class="function-btn">
            <el-button type="warning" size="large" @click="resetData"
              >Reset</el-button
            >
            <el-button type="warning" @click="submitData" :loading="loading">Submit</el-button>
          </section>
        </el-col>
      </el-row>
      <!-- <a
        v-show="false"
        ref="download"
        href="javascript:void(0)"
        @click="downloadExampleFile"
      /> -->
    </div>
  </div>
</template>
<script>
export default {
  data() {
    return {
      uuid: "",
      IntensityFileList: [],
      SeedNodeFileList: [],
      MolecularFileList: [],
      typeMap: {
        "Intensity File": "text/plain; charset=utf-8",
        "Seed Node File": "text/csv; charset=utf-8",
        "Molecular Network File": "text/csv; charset=utf-8",
      },
      elementOptions: [
        {
          value: "C",
          label: "C",
        },
        {
          value: "H",
          label: "H",
        },
        {
          value: "O",
          label: "O",
        },
        {
          value: "N",
          label: "N",
        },
        {
          value: "P",
          label: "P",
        },
        {
          value: "S",
          label: "S",
        },
        {
          value: "F",
          label: "F",
        },
        {
          value: "Cl",
          label: "Cl",
        },
        {
          value: "Br",
          label: "Br",
        },
        {
          value: "I",
          label: "I",
        },
      ],
      formList: {
        compounds_num: "",
        samples_num: "",
        correlation_threshold: "",
        RT_threshold: "",
        ppm_threshold: "",
        is_filter_element: true,
        element_set: [],
        tanimoto_similarity_threshold: "",
        is_max_iterations: true,
        max_iterations: "",
        top_k: "",
      },
      labelMap: {
        compounds_num: "Num. of Compounds",
        samples_num: "Num. of Samples",
        correlation_threshold: "Minimum High-Correlated Score",
        RT_threshold:
          "RT Tolerance for In-source Fragments Identificaton (min)",
        ppm_threshold: "Parent Mass Tolerance for Database Search (ppm)",
        element_set: "Allowed Elements for Database Search",
        tanimoto_similarity_threshold: "Minimum Structure Similarity Score",
        max_iterations: "Maximum Iteration Number of Annotations",
        top_k: "Compound Annotation Result",
      },
      validatorMap: {
        compounds_num: {
          label: "Num. of Compounds",
          type: "number",
        },
        samples_num: {
          label: "Num. of Samples",
          type: "number",
        },
        correlation_threshold: {
          label: "Minimum High-Correlated Score",
          type: "number",
          min: 0,
          max: 1,
        },
        RT_threshold: {
          label: "RT Tolerance for In-source Fragments Identificaton (min)",
          type: "number",
        },
        ppm_threshold: {
          label: "Parent Mass Tolerance for Database Search (ppm)",
          type: "number",
        },
        element_set: {
          label: "Allowed Elements for Database Search",
          type: "array",
        },
        tanimoto_similarity_threshold: {
          label: "Minimum Structure Similarity Score",
          type: "number",
          min: 0,
          max: 1,
        },
        max_iterations: {
          label: "Maximum Iteration Number of Annotations",
          type: "number",
          min: 0,
          max: 100,
        },
        top_k: {
          label: "Compound Annotation Result",
          type: "number",
          min: 0,
          max: 10,
        },
      },
      loading: false,
    };
  },
  mounted() {
    this.uuid = new Date().getTime();
  },
  methods: {
    resetData() {
      this.formList = {
        compounds_num: "",
        samples_num: "",
        correlation_threshold: "",
        RT_threshold: "",
        ppm_threshold: "",
        is_filter_element: true,
        element_set: [],
        tanimoto_similarity_threshold: "",
        is_max_iterations: true,
        max_iterations: "",
        top_k: "",
      };
      this.validatorMap = {
        compounds_num: {
          label: "Num. of Compounds",
          type: "number",
        },
        samples_num: {
          label: "Num. of Samples",
          type: "number",
        },
        correlation_threshold: {
          label: "Minimum High-Correlated Score",
          type: "number",
          min: 0,
          max: 1,
        },
        RT_threshold: {
          label: "RT Tolerance for In-source Fragments Identificaton (min)",
          type: "number",
        },
        ppm_threshold: {
          label: "Parent Mass Tolerance for Database Search (ppm)",
          type: "number",
        },
        element_set: {
          label: "Allowed Elements for Database Search",
          type: "array",
        },
        tanimoto_similarity_threshold: {
          label: "Minimum Structure Similarity Score",
          type: "number",
          min: 0,
          max: 1,
        },
        max_iterations: {
          label: "Maximum Iteration Number of Annotations",
          type: "number",
          min: 0,
          max: 100,
        },
        top_k: {
          label: "Compound Annotation Result",
          type: "number",
          min: 0,
          max: 10,
        },
      };
      this.IntensityFileList = [];
      this.SeedNodeFileList = [];
      this.MolecularFileList = [];
      this.uuid = new Date().getTime();
    },
    dataValidator() {
      // 文件
      if (!this.IntensityFileList.length) {
        this.$message({
          message: `Please input 'Intensity File' !`,
          type: "warning",
        });
        return false;
      }
      if (!this.SeedNodeFileList.length) {
        this.$message({
          message: `Please input 'Seed Node File' !`,
          type: "warning",
        });
        return false;
      }
      if (!this.MolecularFileList.length) {
        this.$message({
          message: `Please input 'Molecular Network File' !`,
          type: "warning",
        });
        return false;
      }
      // 数据
      const checkedFlag = ["is_filter_element", "is_max_iterations"];
      this.$set(
        this.validatorMap,
        "element_set",
        this.formList.is_filter_element
          ? {
              label: "Allowed Elements for Database Search",
              type: "array",
            }
          : null
      );
      this.$set(
        this.validatorMap,
        "max_iterations",
        this.formList.is_max_iterations
          ? {
              label: "Maximum Iteration Number of Annotations",
              type: "number",
              min: 0,
              max: 100,
            }
          : null
      );
      for (let key in this.formList) {
        const validator = this.validatorMap[key];
        // 判断值为空
        if (!checkedFlag.includes(key)) {
          if (
            (!this.formList[key] || !this.formList[key].length) &&
            validator
          ) {
            this.$message({
              message: `Please input '${this.labelMap[key]}' !`,
              type: "warning",
            });
            return false;
          }
          if (this.formList[key] && validator) {
            if (validator.type === "number") {
              const numData = Number(this.formList[key]);
              // 数值判断
              if (!numData) {
                this.$message({
                  message: `The '${validator.label}' must be a Number!`,
                  type: "warning",
                });
                return false;
              }
              // 大小判断
              if (
                validator.max &&
                (numData > validator.max || numData < validator.min)
              ) {
                this.$message({
                  message: `The '${validator.label}' is between ${validator.min} to ${validator.max}!`,
                  type: "warning",
                });
                return false;
              }
            }
          }
        }
      }
      return true;
    },
    fileValidator() {
      if (!this.IntensityFileList.length) {
        this.$message({
          message: `Please input 'Intensity File' !`,
          type: "warning",
        });
        return;
      }
      if (!this.SeedNodeFileList.length) {
        this.$message({
          message: `Please input 'Seed Node File' !`,
          type: "warning",
        });
        return;
      }
      if (!this.MolecularFileList.length) {
        this.$message({
          message: `Please input 'Molecular Network File' !`,
          type: "warning",
        });
        return;
      }
    },
    handleFormData() {
      const params = {};
      const checkedFlag = ["is_filter_element", "is_max_iterations"];
      const checkedLabel = ["element_set", "max_iterations"];
      for (let key in this.formList) {
        if (!checkedFlag.includes(key) && !checkedLabel.includes(key)) {
          params[key] = Number(this.formList[key]);
        } else if (key === "element_set") {
          params[key] = this.formList.is_filter_element
            ? this.formList[key].join(",")
            : "";
        } else if (key === "max_iterations") {
          params[key] = this.formList.is_max_iterations
            ? Number(this.formList[key])
            : 0;
        } else {
          params[key] = this.formList[key];
        }
      }
      return params;
    },
    submitData() {
      // this.fileValidator()
      const canSubmit = this.dataValidator();
      if (!canSubmit) {
        return;
      }
      const params = this.handleFormData();
      this.loading = true;
      this.$axios
        .post(`https://cgran.acemap.info/api/v1/parameter/${this.uuid}`, params)
        .then((res) => {
          const id = res.data.task_id;
          this.loading = false;
          this.resetData();
          this.$router.push({
            name: "result",
            query: {
              id,
            },
          });
        }).catch(res => {
          this.loading=false
          this.$notify.error({
          title: "Error",
          message: res || "Error!Please try again!",
        });
        })
        .catch((res) => {
          this.loading = false;
          this.$notify.error({
            title: "Error",
            message: res || "Error!Please try again!",
          });
        });
    },
    // 下载方法
    downloadFile(type) {
      this.$axios
        .get("https://cgran.acemap.info/api/v1/download/example", {
          params: { file_type: type },
          responseType: "blob", // 关键：指定响应类型为二进制
        })
        .then((res) => {
          // 从响应头获取文件名（如果后端支持）
          const contentDisposition = res.headers["content-disposition"];
          const fileName = contentDisposition
            ? contentDisposition.split("filename=")[1]
            : `ExampleFile-${type}`;

          this.downloadFileFromBlob(res.data, fileName, type);
        });
    },
    // 通用Blob下载方法
    downloadFileFromBlob(blobData, fileName, type) {
      // 创建临时URL
      const url = window.URL.createObjectURL(
        new Blob([blobData], {
          type: this.typeMap[type] || "application/octet-stream",
        })
      );

      // 创建隐藏的下载链接
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      link.style.display = "none";

      // 触发下载
      document.body.appendChild(link);
      link.click();

      // 清理资源
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url); // 释放内存
    },
    IntensityFileSuccess(res, file) {
      this.IntensityFileList.push(file);
    },
    SeedNodeFileSuccess(res, file) {
      this.SeedNodeFileList.push(file);
    },
    MolecularFileSuccess(res, file) {
      this.MolecularFileList.push(file);
    },
  },
};
</script>
<style lang="less" scoped>
.web-container {
  .top-content {
    position: relative;
    h1 {
      font-weight: 800;
      font-size: 44px;
      font-family: system-ui, Avenir, Helvetica, Arial, sans-serif;
      padding: 12px 24px;
    }
    .divider-line {
      height: 6px;
      border-bottom: 2px solid rgb(68 114 196);
      border-top: 2px solid rgb(68 114 196);
    }
    .logo {
      position: absolute;
      top: -40px;
      right: 20px;
    }
  }
  .web-main-content {
    margin: auto;
    max-width: 1980px;
    padding: 24px;
    h2 {
      font-size: 28px;
      text-align: center;
      font-weight: 900;
      margin-top: 24px;
      font-family: system-ui;
    }
  }
  .function-item {
    margin-bottom: 36px;
    header {
      padding: 8px;
      background: rgb(91 155 213);
      color: #fff;
      font-weight: 700;
      font-size: 18px;
      display: flex;
      align-items: center;
      h3 {
        font-family: system-ui, Avenir, Helvetica, Arial, sans-serif;
      }
      .iconfont {
        font-size: 18px;
        margin-right: 4px;
      }
    }
    .item-body {
      background: rgb(237 237 237);
      padding: 16px 36px;
      .input-item {
        position: relative;
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        .el-checkbox {
          position: absolute;
          left: -22px;
        }
      }
      .label {
        font-size: 20px;
        font-weight: 700;
      }
      .tip {
        cursor: pointer;
      }
      .upload-item {
        margin: 0 12px;
      }
      .link {
        font-size: 17px;
        font-style: italic;
        text-decoration: underline;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
        &:hover {
          cursor: pointer;
          color: rgb(37 94 146);
        }
      }
      .no-underline {
        text-decoration: none;
        margin: 0 12px;
      }
      .input-box {
        margin-left: 12px;
        width: 80px;
        &.el-select {
          width: 180px;
        }
      }
    }
  }
  .upload-area {
    .item-body {
      .input-item {
        margin-bottom: 32px;
      }
      .upload-item {
        position: relative;
      }
    }
  }
  .function-btn {
    text-align: center;
    .el-button {
      margin: 0 24px;
      font-size: 18px;
      background: #ed7d31;
      border-color: #ed7d31;
      font-weight: 700;
    }
  }
}
</style>
<style lang="less">
.input-box {
  .el-input__inner {
    text-align: center;
    box-shadow: 2px 1px 10px rgba(0, 0, 0, 0.2);
  }
}
.upload-area {
  .el-upload-list {
    position: absolute;
    top: 26px;
  }
}
.input-item {
  .el-checkbox__input.is-checked .el-checkbox__inner,
  .el-checkbox__input.is-indeterminate .el-checkbox__inner {
    background-color: transparent;
    border-color: #000;
  }
  .el-checkbox__inner {
    vertical-align: middle;
    border: 2px solid #000;
    border-radius: 2px;
    width: 16px;
    height: 16px;
    background-color: transparent;
    &::after {
      border: 2px solid #000;
      border-left: 0;
      border-top: 0;
      height: 10px;
      left: 4px;
      top: -2px;
      width: 5px;
    }
  }
}
.popover-content {
  width: 600px;
  p {
    margin-bottom: 6px;
    line-height: 18px;
    font-size: 14px;
  }
  a {
    color: #ed7d31;
  }
}
</style>